// eslint-disable-next-line no-unused-vars
import React from 'react';
import { Link } from 'react-router-dom';
import { func, node, string, bool } from 'prop-types';
import { Button, Loading } from '@carbon/react';
import { useTranslation } from 'react-i18next';
import styled from '@emotion/styled';
import { useTheme } from '@emotion/react';
import { ShoppingCart } from 'lucide-react';

import ProductSkeletonItem from 'Components/Products/ProductSkeletonItem';
import WishlistsDropdown from 'Containers/Wishlists/WishlistsDropdown';
import { stockAlertWishlistId } from 'Contexts/WishlistContext';
import usePermissions from 'Hooks/usePermissions';
import { PERMISSION } from 'Constants';

const StyledWishlistProductSkeletonItem = styled(ProductSkeletonItem)`
  padding: 3px 15px 15px;

  .skeleton {
    display: flex;
  }

  &:not(:last-of-type) {
    border-bottom: 1px solid ${ props => props.theme.products.colors.border };
  }

  > div:first-of-type {
    .cds--skeleton__placeholder {
      max-height: 50px;
      max-width: 50px;
    }
  }

  > div:nth-of-type(2) {
    .cds--skeleton__text:last-of-type {
      display: none;
    }
  }
`;

const StyledWishlistMenu = styled.div`
  position: relative;

  .productList {
    padding: 0 15px;
    //max-height: calc(100vh - 450px) !important;
  }

  .productList + footer {
    > button,
    > a {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 100%;
      max-width: 255px;
      height: 40px !important;
      min-height: 0 !important;
      padding: 0;
      text-align: center;
      font-size: 1rem;
      font-weight: normal;
    }

    .addAll {
      > svg {
        margin-right: 8px;
      }

      color: ${ props => props.theme.colors.white } !important;
      border: 0 !important;

      .cds--loading {
        margin-left: 5px;

        svg {
          height: 16px;
        }
      }
    }

    .seeAll {
      border: 1px solid ${ props => props.theme.colors.blue };
      color: ${ props => props.theme.colors.blue };
    }
  }
`;

const StyledEmptyContainer = styled.p`
  padding: 1rem 15px;
  text-align: left;
  font-size: 1rem;
  font-weight: 600;
  line-height: 1rem;
  max-height: 14rem;
`;

const WishlistMenuComponent = props => {
  const {
    children,
    wishlistErr,
    err,
    currentList,
    addAllToCartLoading,
    onAddListToCart,
    getWishlist,
    canAddAll,
    loadingList
  } = props;

  const theme = useTheme();
  const { t } = useTranslation();
  const { can } = usePermissions();
  const canAddToCart = can(PERMISSION.ADD_TO_CART);

  const getEmptyRef = message => (
    <StyledEmptyContainer>
      { message }
    </StyledEmptyContainer>
  );

  return (
    <>
      <WishlistsDropdown onChangeList={ getWishlist } showSelectedIcon={ false } />
      { loadingList && (
        <>
          <StyledWishlistProductSkeletonItem />
          <StyledWishlistProductSkeletonItem />
          <StyledWishlistProductSkeletonItem />
        </>
      ) }
      {/* eslint-disable-next-line no-nested-ternary */ }
      { (!loadingList && children?.length)
        ? (
          <StyledWishlistMenu theme={ theme }>
            <div className="productList">
              { children }
            </div>
            <footer>
              { currentList !== stockAlertWishlistId && (
                <Button
                  className="addAll"
                  kind="danger"
                  disabled={
                    !canAddAll
                    || !canAddToCart
                    || addAllToCartLoading
                  }
                  onClick={ onAddListToCart }
                  data-test="onAddAllToCart"
                >
                  { addAllToCartLoading && (
                    <Loading active={ addAllToCartLoading } small withOverlay={ false } />
                  ) }
                  <ShoppingCart />
                  { t('wishlist.addToCart') }
                </Button>
              ) }
              <Link className="seeAll" to={ `/lists/${currentList}` }>
                { t('wishlists.continueToLists') } &gt;
              </Link>
            </footer>
          </StyledWishlistMenu>
        )
        : !loadingList && getEmptyRef(err || wishlistErr || t('wishlists.empty'))
      }
    </>
  );
};

WishlistMenuComponent.propTypes = {
  children: node,
  loadingList: bool,
  getWishlist: func,
  addAllToCartLoading: bool,
  err: string,
  wishlistErr: string,
  canAddAll: bool,
  onAddListToCart: func,
  currentList: string
};
WishlistMenuComponent.displayName = 'WishlistMenuComponent';

export default WishlistMenuComponent;
